import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Tooltip,
  CircularProgress,
  Alert,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Grid,
  Switch,
  Stack,
  Chip,
  alpha,
  InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import TableSortLabel from '@mui/material/TableSortLabel';
import { getCustomers, getCustomerStatistics, updateCustomerStatus, getCompanies } from '../services/api';
import EditCustomerModal from '../components/modals/EditCustomerModal';
import AddCustomerModal from '../components/modals/AddCustomerModal';
import { neonBlue, shakespeare, kepple, california, stormGrey } from '../theme/colors';

const StatTile = ({ title, value, icon: Icon, accent, loading }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      height: '100%',
      width: '100%',
      borderRadius: 2,
      border: `1px solid ${alpha(accent, 0.12)}`,
      background: `linear-gradient(135deg, ${alpha(accent, 0.08)} 0%, #ffffff 60%)`,
      position: 'relative',
      overflow: 'hidden',
      transition: 'box-shadow 0.2s, transform 0.2s',
      '&:hover': {
        boxShadow: `0 8px 24px ${alpha(accent, 0.16)}`,
        transform: 'translateY(-2px)',
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 4,
        bgcolor: accent,
      },
    }}
  >
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Box>
        <Typography variant="caption" sx={{ color: stormGrey[500], fontWeight: 500 }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: '2rem', fontWeight: 700, lineHeight: 1.2, color: accent, mt: 0.5 }}>
          {loading ? <CircularProgress size={22} sx={{ color: accent }} /> : value}
        </Typography>
      </Box>
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: alpha(accent, 0.14),
          color: accent,
        }}
      >
        <Icon />
      </Box>
    </Stack>
  </Paper>
);

const CustomersPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusError, setStatusError] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [statistics, setStatistics] = useState({
    totalCustomers: 0,
    totalIndividualCustomers: 0,
    totalCompanyCustomers: 0,
    totalRepeatCustomers: 0
  });
  const [loadingStats, setLoadingStats] = useState(true);
  const [sortField, setSortField] = useState('name'); // Default sort by name
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  const recordsPerPage = 25;
  const userRole = localStorage.getItem('userRole');
  const isSuperAdmin = userRole === 'super-admin';
  const isCusAdmin = userRole === 'cus-admin';
  const currentUserCompanyId = localStorage.getItem('companyId');

  // Get company filter from URL query params
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const companyId = queryParams.get('company');
    if (companyId) {
      setSelectedCompany(companyId);
    } else {
      setSelectedCompany('');
    }
  }, [location.search]);

  const fetchCustomers = useCallback(async () => {
    try {
      const params = {};
      if (searchTerm) {
        params.search = searchTerm;
      }
      if (isSuperAdmin && selectedCompany) {
        params.company_id = selectedCompany;
      }
      const response = await getCustomers(params);
      let customers = response.data || [];
      
      setCustomers(customers);
      setError('');
      
      // Calculate statistics from customers list as fallback/backup
      // This ensures stats are shown even if API fails
      if (!searchTerm) { // Only calculate from list if no search filter (to match API behavior)
        const calculatedStats = {
          totalCustomers: customers.length,
          totalIndividualCustomers: customers.filter(c => c.type === 1).length,
          totalCompanyCustomers: customers.filter(c => c.type === 2).length,
          totalRepeatCustomers: customers.filter(c => c.type === 1 && (c.order_count || 0) > 1).length
        };
        
        // Update statistics if API hasn't returned data yet
        setStatistics(prev => {
          if (prev.totalCustomers === 0) {
            return calculatedStats;
          }
          return prev;
        });
      }
    } catch (err) {
      setError('Failed to fetch customers');
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, selectedCompany, isSuperAdmin]);

  const fetchCompanies = useCallback(async () => {
    if (isSuperAdmin || isCusAdmin) {
      try {
        const response = await getCompanies();
        setCompanies(response.data);
      } catch (err) {
      }
    }
  }, [isSuperAdmin, isCusAdmin]);

  const fetchStatistics = useCallback(async () => {
    try {
      setLoadingStats(true);
      const params = {};
      if (isSuperAdmin && selectedCompany) {
        params.company_id = selectedCompany;
      }
      
      const response = await getCustomerStatistics(params);
      
      // Handle response - axios wraps the response in .data
      const statsData = response?.data;
      
      if (statsData && typeof statsData === 'object') {
        setStatistics({
          totalCustomers: Number(statsData.totalCustomers) || 0,
          totalIndividualCustomers: Number(statsData.totalIndividualCustomers) || 0,
          totalCompanyCustomers: Number(statsData.totalCompanyCustomers) || 0,
          totalRepeatCustomers: Number(statsData.totalRepeatCustomers) || 0
        });
      }
      setLoadingStats(false);
    } catch (err) {
      setLoadingStats(false);
      // Set statistics to 0 on error to show something
      setStatistics({
        totalCustomers: 0,
        totalIndividualCustomers: 0,
        totalCompanyCustomers: 0,
        totalRepeatCustomers: 0
      });
    }
  }, [selectedCompany, isSuperAdmin]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  // Refetch statistics when customers list changes
  useEffect(() => {
    if (!loading) {
      fetchStatistics();
    }
  }, [customers.length, loading, fetchStatistics]);

  const handleStatusChange = async (e, customerId, isActive) => {
    e.stopPropagation();
    setStatusError('');

    try {
      await updateCustomerStatus(customerId, isActive);
      // Update the customer in the list
      setCustomers(customers.map(customer => 
        customer.id === customerId 
          ? { ...customer, is_active: isActive }
          : customer
      ));
      // Refetch statistics after status change
      fetchStatistics();
    } catch (err) {
      setStatusError(err.response?.data?.message || 'Failed to update customer status');
      // Revert the change on error
      setCustomers(customers.map(customer => 
        customer.id === customerId 
          ? { ...customer, is_active: !isActive }
          : customer
      ));
    }
  };

  const handleRowClick = (customer) => {
    setSelectedCustomer(customer);
    setIsEditModalOpen(true);
  };

  const handleCustomerCreated = (newCustomer) => {
    setCustomers([newCustomer, ...customers]);
    setIsAddModalOpen(false);
    // Refetch statistics after creating a customer
    fetchStatistics();
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedCustomer(null);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleCompanyChange = (event) => {
    const companyId = event.target.value;
    setSelectedCompany(companyId);
    setCurrentPage(1); // Reset to first page when filtering
    
    // Update URL query params
    const queryParams = new URLSearchParams(location.search);
    if (companyId) {
      queryParams.set('company', companyId);
    } else {
      queryParams.delete('company');
    }
    
    const newSearch = queryParams.toString();
    navigate(`/customers${newSearch ? `?${newSearch}` : ''}`, { replace: true });
  };

  // Handle column sorting
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  // Sort customers based on sortField and sortDirection
  const sortedCustomers = useMemo(() => {
    const sorted = [...customers].sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'name':
          aValue = (a.name || '').toLowerCase();
          bValue = (b.name || '').toLowerCase();
          break;
        case 'phone':
          aValue = a.phone || '';
          bValue = b.phone || '';
          break;
        case 'address':
          aValue = (a.address || '').toLowerCase();
          bValue = (b.address || '').toLowerCase();
          break;
        case 'type':
          aValue = a.type === 1 ? 'Individual' : 'Company';
          bValue = b.type === 1 ? 'Individual' : 'Company';
          break;
        case 'order_count':
          aValue = a.order_count || 0;
          bValue = b.order_count || 0;
          break;
        case 'company':
          aValue = (a.company?.name || '').toLowerCase();
          bValue = (b.company?.name || '').toLowerCase();
          break;
        case 'is_active':
          aValue = a.is_active !== false ? 1 : 0; // true = 1, false = 0
          bValue = b.is_active !== false ? 1 : 0;
          break;
        default:
          return 0;
      }

      // Handle comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      } else {
        // Numeric comparison
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
    });

    return sorted;
  }, [customers, sortField, sortDirection]);

  // Calculate pagination
  const paginatedCustomers = useMemo(() => {
    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    return sortedCustomers.slice(indexOfFirstRecord, indexOfLastRecord);
  }, [sortedCustomers, currentPage]);

  const totalPages = Math.ceil(sortedCustomers.length / recordsPerPage);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress sx={{ color: neonBlue[500] }} />
      </Box>
    );
  }

  const sortableHeader = (field, label, align) => (
    <TableCell
      align={align}
      sx={{ cursor: 'pointer', fontWeight: 600, color: stormGrey[700], whiteSpace: 'nowrap' }}
      onClick={() => handleSort(field)}
    >
      <TableSortLabel active={sortField === field} direction={sortField === field ? sortDirection : 'asc'}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: { xs: 2, sm: 2.5 },
          borderRadius: 2,
          background: `linear-gradient(135deg, ${neonBlue[600]} 0%, ${neonBlue[500]} 50%, ${shakespeare[500]} 100%)`,
          color: '#fff',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
              Customer Management
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, color: alpha('#fff', 0.85) }}>
              Manage customer records, status, and order history
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
            {isSuperAdmin && (
              <FormControl
                size="small"
                sx={{
                  minWidth: 220,
                  bgcolor: alpha('#fff', 0.95),
                  borderRadius: 1,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
                }}
              >
                <InputLabel>Filter by Company</InputLabel>
                <Select
                  value={selectedCompany}
                  onChange={handleCompanyChange}
                  label="Filter by Company"
                >
                  <MenuItem value="">
                    <em>All Companies</em>
                  </MenuItem>
                  {companies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setIsAddModalOpen(true)}
              sx={{
                bgcolor: '#fff',
                color: neonBlue[600],
                fontWeight: 600,
                '&:hover': { bgcolor: alpha('#fff', 0.9) },
              }}
            >
              Add Customer
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Grid container spacing={2.5} sx={{ mb: 3, width: '100%' }}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            title="Total Customers"
            value={statistics.totalCustomers}
            icon={GroupsIcon}
            accent={neonBlue[500]}
            loading={loadingStats}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            title="Individual Customers"
            value={statistics.totalIndividualCustomers}
            icon={PersonIcon}
            accent={shakespeare[500]}
            loading={loadingStats}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            title="Company Customers"
            value={statistics.totalCompanyCustomers}
            icon={BusinessIcon}
            accent={kepple[500]}
            loading={loadingStats}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            title="Repeat Customers"
            value={statistics.totalRepeatCustomers}
            icon={AutorenewIcon}
            accent={california[500]}
            loading={loadingStats}
          />
        </Grid>
      </Grid>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          border: `1px solid ${stormGrey[200]}`,
          bgcolor: '#fff',
        }}
      >
        <TextField
          fullWidth
          placeholder="Search customers by name or phone..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: stormGrey[400] }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: stormGrey[50],
            },
          }}
        />
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
      {statusError && <Alert severity="error" sx={{ mb: 1.5 }}>{statusError}</Alert>}

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          width: '100%',
          borderRadius: 2,
          border: `1px solid ${stormGrey[200]}`,
          overflow: 'hidden',
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: alpha(neonBlue[500], 0.06) }}>
              {sortableHeader('name', 'Name')}
              {sortableHeader('phone', 'Phone')}
              {sortableHeader('address', 'Address')}
              {sortableHeader('type', 'Type')}
              {sortableHeader('order_count', 'Orders', 'center')}
              {isSuperAdmin && sortableHeader('company', 'Company')}
              {sortableHeader('is_active', 'Status', 'center')}
              <TableCell sx={{ fontWeight: 600, color: stormGrey[700], width: 80 }} align="center">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {customers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isSuperAdmin ? 8 : 7} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No customers found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedCustomers.map((customer, index) => (
                <TableRow
                  key={customer.id}
                  hover
                  onClick={() => handleRowClick(customer)}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: index % 2 === 1 ? alpha(stormGrey[100], 0.5) : '#fff',
                    '&:hover': { bgcolor: alpha(neonBlue[500], 0.06) },
                    '& td': { borderBottom: `1px solid ${stormGrey[200]}` },
                  }}
                >
                  <TableCell
                    sx={{
                      maxWidth: 180,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontWeight: 500,
                    }}
                    title={customer.name}
                  >
                    {customer.name}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{customer.phone}</TableCell>
                  <TableCell
                    sx={{
                      maxWidth: 260,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      color: stormGrey[600],
                    }}
                    title={customer.address || ''}
                  >
                    {customer.address || '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={customer.type === 1 ? 'Individual' : 'Company'}
                      size="small"
                      sx={{
                        bgcolor: customer.type === 1 ? alpha(shakespeare[500], 0.12) : alpha(kepple[500], 0.12),
                        color: customer.type === 1 ? shakespeare[500] : kepple[600],
                        fontWeight: 500,
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={customer.order_count || 0}
                      size="small"
                      variant="outlined"
                      sx={{ minWidth: 36, fontWeight: 600, borderColor: alpha(neonBlue[500], 0.3) }}
                    />
                  </TableCell>
                  {isSuperAdmin && (
                    <TableCell sx={{ color: stormGrey[600] }}>
                      {customer.company?.name || '-'}
                    </TableCell>
                  )}
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <Switch
                      checked={customer.is_active !== false}
                      onChange={(e) => handleStatusChange(e, customer.id, e.target.checked)}
                      color="primary"
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Edit Customer">
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRowClick(customer);
                        }}
                        size="small"
                        sx={{
                          color: neonBlue[500],
                          bgcolor: alpha(neonBlue[500], 0.08),
                          '&:hover': { bgcolor: alpha(neonBlue[500], 0.16) },
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {sortedCustomers.length > recordsPerPage && (
        <Box display="flex" justifyContent="center" mt={1.5}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(event, value) => setCurrentPage(value)}
            color="primary"
          />
        </Box>
      )}


      <EditCustomerModal
        open={isEditModalOpen}
        onClose={handleCloseEditModal}
        customer={selectedCustomer}
        onUpdate={(updatedCustomer) => {
          setCustomers(customers.map(c => c.id === updatedCustomer.id ? updatedCustomer : c));
          handleCloseEditModal();
        }}
        companies={companies}
        isSuperAdmin={isSuperAdmin}
        isCusAdmin={isCusAdmin}
        currentUserCompanyId={currentUserCompanyId}
      />

      <AddCustomerModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onCustomerCreated={handleCustomerCreated}
        companies={companies}
        isSuperAdmin={isSuperAdmin}
        isCusAdmin={isCusAdmin}
        currentUserCompanyId={currentUserCompanyId}
      />
    </Box>
  );
};

export default CustomersPage;

